/**
 * When the browser agent may act on its own (ADMIN-220). Pure: `guard.ts` gathers the facts (the
 * chat's mode, the site's rule, what the action would touch) and asks `decide`.
 *
 * - **Modes, per chat:** Read only (the agent reads pages, never acts), Ask before acting (the
 *   default: it asks the first time it acts on a site in a chat), Act without asking.
 * - **Site rules:** Allow / Ask / Deny for a site and its subdomains. Deny wins over everything,
 *   reading included; Ask asks before every action there; Allow never asks.
 * - **Sensitive actions** (a submit, a purchase, a send or post, a payment form) always ask,
 *   whatever the mode and rules, unless the site is denied (then they are refused).
 */

export type AgentMode = "read" | "ask" | "act";
export type SiteRule = "allow" | "ask" | "deny";

export const DEFAULT_MODE: AgentMode = "ask";

export const AGENT_MODES: { id: AgentMode; name: string; description: string }[] = [
  { id: "read", name: "Read only", description: "Reads pages, never clicks or types" },
  { id: "ask", name: "Ask before acting", description: "Asks the first time on each site" },
  { id: "act", name: "Act without asking", description: "Only asks before sensitive actions" },
];

export function isAgentMode(value: unknown): value is AgentMode {
  return value === "read" || value === "ask" || value === "act";
}

export function isSiteRule(value: unknown): value is SiteRule {
  return value === "allow" || value === "ask" || value === "deny";
}

/** Tools that change something: a page, a tab, where a tab is. Every other tool only reads. */
const ACTING_TOOLS = new Set(["click", "type", "select", "navigate", "open_tab", "close_tab"]);

function isActingTool(toolName: string): boolean {
  return ACTING_TOOLS.has(toolName);
}

/** The site a URL belongs to, as rules name it: its hostname, lowercased, without `www.`. */
export function siteOf(url: string | undefined | null): string | null {
  if (!url) return null;
  try {
    const { protocol, hostname } = new URL(url);
    if (protocol !== "http:" && protocol !== "https:") return null;
    return hostname.toLowerCase().replace(/^www\./, "") || null;
  } catch {
    return null;
  }
}

/** A site as typed in Settings (`shop.example`, `https://www.shop.example/cart`) → its rule key. */
export function normalizeSite(text: string): string | null {
  const trimmed = text.trim().toLowerCase();
  if (!trimmed) return null;
  const site = siteOf(/^[a-z][a-z0-9+.-]*:\/\//.test(trimmed) ? trimmed : `https://${trimmed}`);
  return site && /^[a-z0-9.-]+$/.test(site) && site.includes(".") ? site : null;
}

/**
 * The rule for a site: its own, or its parent domains' (`mail.example.com` takes `example.com`'s).
 * When several match, Deny wins, then Ask, then Allow.
 */
export function ruleFor(rules: Record<string, SiteRule>, site: string | null): SiteRule | null {
  if (!site) return null;
  const parts = site.split(".");
  const found = new Set<SiteRule>();
  for (let i = 0; i < parts.length - 1; i++) {
    const rule = rules[parts.slice(i).join(".")];
    if (rule) found.add(rule);
  }
  if (found.has("deny")) return "deny";
  if (found.has("ask")) return "ask";
  if (found.has("allow")) return "allow";
  return null;
}

/** Why an action is sensitive, from `sensitive.ts`. */
export type SensitiveKind = "purchase" | "payment" | "send" | "submit" | "delete" | "account";

export type Sensitivity = { kind: SensitiveKind; label?: string | undefined } | null;

export type AskReason = "mode" | "site_rule" | "sensitive";

export type Decision =
  | { kind: "run" }
  | { kind: "ask"; reason: AskReason }
  | { kind: "deny"; reason: "read_only" | "site_denied" };

export function decide(facts: {
  toolName: string;
  mode: AgentMode;
  /** The site's rule (`ruleFor`), or null when it has none. */
  rule: SiteRule | null;
  /** The user allowed acting on this site for the rest of this chat. */
  granted: boolean;
  sensitive: Sensitivity;
}): Decision {
  if (facts.rule === "deny") return { kind: "deny", reason: "site_denied" };
  if (!isActingTool(facts.toolName)) return { kind: "run" };
  if (facts.mode === "read") return { kind: "deny", reason: "read_only" };
  if (facts.sensitive) return { kind: "ask", reason: "sensitive" };
  if (facts.rule === "ask") return { kind: "ask", reason: "site_rule" };
  if (facts.rule === "allow" || facts.mode === "act" || facts.granted) return { kind: "run" };
  return { kind: "ask", reason: "mode" };
}

/** What the approval card offers. A sensitive action can only be allowed once. */
export type ApprovalChoice = "once" | "site" | "deny";

export function choicesFor(reason: AskReason): ApprovalChoice[] {
  return reason === "mode" ? ["once", "site", "deny"] : ["once", "deny"];
}
